// Modules
import { useContext, useEffect, useState, useReducer } from 'react';

// Context
import { useAuthState } from '../../providers/authProvider';
import { UserDetailContext, UserDetailContextProps } from '../../context/userDetail/userDetailContext';

// Api
import { getContactSummary } from '../../common/api/contactApi';

// Components
import { GridFilter } from '../gridfilter/GridFilter';
import { StyledTitle, FilterRowWrapper, GridTypeSelector } from '../../common/components';
import { ContactSummaryGrid } from './ContactSummaryGrid';
import FormElement from '../../common/components/FormElement';

// Utils
import { getInitialType } from '../../utils/getInitialTypes';
import { gridReducer, GRID_ACTIONS, IGridReducerState } from '../../reducer/gridReducer/gridReducer';
import { getSelectedCustomerId } from '../../helpers/customer';
import { formatDate } from '../utils/date/date';
import { isTpiUser } from '../../helpers/tpiUser';

// Types
import { EssAccountsTypes } from '../../types/account';
import moment from 'moment';
import { NotificationBox } from '../health/NotificationBox/NotificationBox';
import { ContractSummaryModal } from '../mymessages/modalTemplates/ContractSummaryModal';
import { SearchDetailContext, SearchDetailContextProps } from '../../context/searchDetail/searchDetailContext';
import { RoutingToTypes } from '../search/SearchActions/SearchActions';
import { NotificationBoxV2 } from '../health/NotificationBoxV2/NotificationBoxV2';

export const ContactSummary = () => {
  const authContext = useAuthState();
  const { userDetail } = useContext<UserDetailContextProps>(UserDetailContext);
  const searchContext = useContext<SearchDetailContextProps>(SearchDetailContext);

  const [gridTypes, setGridTypes] = useState<string[]>();
  const [isContractDueToExpire, setIsContractDueToExpire] = useState<boolean>(false);
  const [isContractDeemed, setIsContractDeemed] = useState<boolean>(false);
  const [isContractPending, setIsContractPending] = useState<boolean>(false);
  const [isDirectCustomer, setIsDirectCustomer] = useState<boolean>(false);
  const [isDirectSoldByBrokerCustomer, setIsDirectSoldByBrokerCustomer] = useState<boolean>(false);
  const [showContractSummaryModal, setShowContractSummaryModal] = useState<boolean>(false);
  const [contractStatus, setContractStatus] = useState<any>('Live');

  const resolution = window.innerWidth;
  const isMobile = resolution <= 540;
  const isDesktop = !isMobile;
  const isTpiCustomer = isTpiUser();
  const [contractStatusElements, setContractStatusElements] = useState<Array<Status>>(['Live']);

  const initialState: IGridReducerState = {
    page: 1,
    currentType: getInitialType(userDetail.hasElectricity, userDetail.hasGas),
    data: {},
    gridData: [],
    initialSortField: 'contract-end-date',
    initialSortOrder: 'ASC',
    currentSortField: 'contract-end-date',
    currentSortOrder: 'ASC',
    isLoading: false,
    hasMoreData: true,
    selectedCustomerId: getSelectedCustomerId(userDetail),
  };

  const [state, dispatch] = useReducer(gridReducer, initialState);

  const showWarningMessage =
    (isContractDeemed || isContractDueToExpire) && !isContractPending && contractStatus != 'Future';

  const currentSort: Common.ISort = { field: state.currentSortField, order: state.currentSortOrder };

  type Status = 'Pending' | 'Future' | 'Live';

  let isInitialising = false;

  useEffect(() => {
    if (searchContext.searchDetail != null && searchContext.searchDetail.searchText.length > 0) {
      // filter by search result

      if (searchContext.searchDetail.type && searchContext.searchDetail.routingTo === RoutingToTypes.contracts) {
        const type = searchContext.searchDetail.type.toLowerCase();

        if (type === 'account') {
          dispatch({
            type: GRID_ACTIONS.FILTER_BY_CUSTOMER,
            payload: { essCustomerId: searchContext.searchDetail.accountId },
          });
        }
        // Clear Search Detail
        searchContext.setSearchDetail({ searchText: '' });
      }
    }
    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchContext.searchDetail.type]);

  useEffect(() => {
    handleResetFields();

    isInitialising = true;

    if (!state.selectedCustomerId) {
      if (userDetail.customerAccounts.length == 1) {
        initialState.selectedCustomerId = userDetail.customerAccounts[0].essAccountID;
      } else {
        return;
      }
    } else {
      initialState.selectedCustomerId = state.selectedCustomerId;
    }

    let types = [];
    if (userDetail.hasElectricity) types.push(EssAccountsTypes.Electricity);
    if (userDetail.hasGas) types.push(EssAccountsTypes.Gas);

    setGridTypes(types);

    // When navigating directly to url
    initialState.currentType = getInitialType(userDetail.hasElectricity, userDetail.hasGas, userDetail.hasRenewable);

    initialState.page = 1;

    if (userDetail.customerAccounts.length === 1) {
      dispatch({ type: GRID_ACTIONS.REINIT, payload: initialState });
    }

    initialiseLoadData().then(() => {
      isInitialising = false;
    });

    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedCustomerId, userDetail.customerAccounts]);

  useEffect(() => {
    if (isInitialising) return;

    // Don't load if no customer selected
    if (!state.selectedCustomerId) return;

    loadData(contractStatus);

    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.page, state.currentSortField, state.currentSortOrder]);

  const handleGetContractSummary = (status: 'Pending' | 'Future Pending' | 'Live') => {
    if (!state.selectedCustomerId) return;

    const tempResult: any = getContactSummary(
      process.env.REACT_APP_API_URL,
      authContext,
      state.selectedCustomerId,
      status,
      state.page,
      currentSort
    );
    return tempResult;
  };

  const formatData = (futureStatus: boolean, responseLive: any): any => {
    let anyPendingContracts = responseLive.some(
      (type: { [x: string]: string }) =>
        type['contract-status'].toUpperCase() === 'PENDING' ||
        type['contract-status'].toUpperCase() === 'FUTURE PENDING'
    );

    responseLive.map((item: Api.IContactSummaryPagedData) => {
      item['contract-start-date'] = formatDate(item['contract-start-date']);
      item['contract-end-date'] = formatDate(item['contract-end-date']);

      item.warningIcon = '';

      if (item['product-code'].toUpperCase() === 'DEEMED') {
        item.warningIcon = !anyPendingContracts ? 'C2' : '';
        setIsContractDeemed(true);
      }

      // Show Warning if 60 days or less left
      const now = moment();
      const contractEndDate = moment(item['contract-end-date'], 'DD/MM/YYYY');
      const diffDays = contractEndDate.diff(now, 'days');

      if (diffDays <= 60) {
        item.warningIcon = !anyPendingContracts && !futureStatus ? 'C1' : '';
        setIsContractDueToExpire(true);
      }

      if (
        item['contract-status'].toUpperCase() === 'PENDING' ||
        item['contract-status'].toUpperCase() === 'FUTURE PENDING'
      ) {
        setIsContractPending(true);
      }

      if (item['market-sector'] === 'Third Party Intermediaries') {
        setIsDirectSoldByBrokerCustomer(true);
      } else if (
        item['market-sector'] === 'Direct' ||
        item['market-sector'] === 'Face to Face' ||
        item['market-sector'] === 'Telesales'
      ) {
        setIsDirectCustomer(true);
      }
    });

    return responseLive;
  };

  const populateRadioButtons = (responseLive: any, responsePending: any, responseFuture: any) => {
    const localContractStatusElements: Array<Status> = [];

    if (responseLive && responseLive.length > 0) {
      localContractStatusElements.push('Live');
    }
    if (responsePending && responsePending.length > 0) {
      localContractStatusElements.push('Pending');
    }
    if (responseFuture && responseFuture.length > 0) {
      localContractStatusElements.push('Future');
    }
    setContractStatusElements(localContractStatusElements);
  };

  const initialiseLoadData = async () => {
    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: true });

    dispatch({ type: GRID_ACTIONS.CLEAR_DATA });

    const pendingPromise = handleGetContractSummary('Pending');
    const futurePromise = handleGetContractSummary('Future Pending');
    const livePromise = handleGetContractSummary('Live');

    const [responsePending, responseFuture, responseLive] = await Promise.all([
      pendingPromise,
      futurePromise,
      livePromise,
    ]);

    populateRadioButtons(responseLive, responsePending, responseFuture);

    // Populate Grid
    if (responseLive && responseLive.length > 0) {
      const formattedLiveData = formatData(false, responseLive);
      dispatch({ type: GRID_ACTIONS.ADD_DATA, payload: { data: formattedLiveData, page: state.page } });
    } else if (responsePending && responsePending.length > 0) {
      const formattedPendingData = formatData(false, responsePending);
      dispatch({ type: GRID_ACTIONS.ADD_DATA, payload: { data: formattedPendingData, page: state.page } });
    } else if (responseFuture && responseFuture.length > 0) {
      const formattedFutureData = formatData(true, responseFuture);
      dispatch({ type: GRID_ACTIONS.ADD_DATA, payload: { data: formattedFutureData, page: state.page } });
    }

    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
  };

  const loadData = async (status: Status) => {
    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: true });

    if (status == 'Future') {
      const futurePromise = handleGetContractSummary('Future Pending');

      const [responseFuture] = await Promise.all([futurePromise]);

      const formattedFutureData = formatData(true, responseFuture);

      dispatch({ type: GRID_ACTIONS.ADD_DATA, payload: { data: formattedFutureData, page: state.page } });
    } else if (status == 'Pending') {
      const pendingPromise = handleGetContractSummary('Pending');

      const [responsePending] = await Promise.all([pendingPromise]);

      const formattedPendingData = formatData(false, responsePending);

      dispatch({ type: GRID_ACTIONS.ADD_DATA, payload: { data: formattedPendingData, page: state.page } });
    } else if (status == 'Live') {
      const livePromise = handleGetContractSummary('Live');

      const [responseLive] = await Promise.all([livePromise]);

      const formattedLiveData = formatData(false, responseLive);

      dispatch({ type: GRID_ACTIONS.ADD_DATA, payload: { data: formattedLiveData, page: state.page } });
    }

    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
  };

  const handleTypeChange = (type: string) => {
    dispatch({ type: GRID_ACTIONS.CHANGE_TYPE, payload: type });
  };

  const handleSortChange = (sort: Common.ISort) => {
    dispatch({ type: GRID_ACTIONS.CHANGE_SORT, payload: sort });
  };

  const handleCustomerFilterChange = (option: Common.IOption) => {
    setContractStatus('Live');
    dispatch({ type: GRID_ACTIONS.FILTER_BY_CUSTOMER, payload: { essCustomerId: option.value } });
  };

  const handleResetFields = () => {
    setIsContractDeemed(false);
    setIsContractDueToExpire(false);
    setIsContractPending(false);
  };

  const handleClearAll = () => {
    dispatch({ type: GRID_ACTIONS.CLEAR_FILTERS });
  };

  const handlePagingChange = () => {
    dispatch({ type: GRID_ACTIONS.INCREMENT_PAGE });
  };

  const requestAQuoteClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setShowContractSummaryModal(true);
  };

  const handleRequestAQuoteHide = (event: any) => {
    setShowContractSummaryModal(false);
  };

  const handleContractStatusChange = (status: Status) => {
    setContractStatus(status);

    handleResetFields();

    if (state.page > 1) {
      dispatch({ type: GRID_ACTIONS.RESET_PAGE });
    } else {
      loadData(status);
    }
  };

  return (
    <>
      {/* The below FilterRowWrapper and GridFilter will only return for mobile */}
      {isMobile && (
        <div>
          <FilterRowWrapper>
            <StyledTitle>Contract summary</StyledTitle>
          </FilterRowWrapper>
          <GridFilter
            showClearAll={false}
            showSites={false}
            showAccount={false}
            siteData={state.data?.sites}
            selectedType={state.currentType}
            handleSiteFilterChange={() => {}}
            accountData={state.data?.accounts}
            customerData={state.data?.customers}
            handleClearAllClick={handleClearAll}
            handleAccountFilterChange={() => {}}
            handleCustomerFilterChange={handleCustomerFilterChange}
          />

          {contractStatusElements.length === 1 && (
            <div className='pt-1 pb-4 pl-4'>
              <span className='pr-2' style={{ color: '#216EF2' }}>
                Contract status:
              </span>
              {contractStatusElements[0]}
            </div>
          )}
          {contractStatusElements.length > 1 && (
            <div className='pb-2 pl-4'>
              <FormElement.RadioRow
                id='page-new-landlord'
                className={'pr-4'}
                label={'Contract status'}
                name='newlandlord'
                elements={contractStatusElements}
                value={contractStatus}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleContractStatusChange(e.target.value as Status);
                }}
                required={true}
                style={{ color: '#216EF2' }}
              />
            </div>
          )}
          <div className='col-12'>
            {showWarningMessage && (
              <>
                {isContractDueToExpire && (
                  <NotificationBoxV2
                    type='C1'
                    showButton={true}
                    boxText='You have contracts that are due to expire...'
                    buttonText='contact us'
                    onClick={requestAQuoteClick}
                  ></NotificationBoxV2>
                )}
                {isContractDeemed && (
                  <NotificationBoxV2
                    type='C2'
                    showButton={true}
                    boxText='You have contracts that are on Deemed rates...'
                    buttonText='contact us'
                    onClick={requestAQuoteClick}
                  ></NotificationBoxV2>
                )}
              </>
            )}
          </div>
        </div>
      )}
      <div className='container-fluid'>
        <div className='row'>
          {/* The below FilterRowWrapper and GridFilter will only return for desktop */}
          {isDesktop && (
            <div className='col-md-12'>
              <FilterRowWrapper>
                <StyledTitle>Contract summary</StyledTitle>
              </FilterRowWrapper>
              <GridFilter
                showClearAll={false}
                showSites={false}
                showAccount={false}
                siteData={state.data?.sites}
                selectedType={state.currentType}
                handleSiteFilterChange={() => {}}
                accountData={state.data?.accounts}
                customerData={state.data?.customers}
                handleClearAllClick={handleClearAll}
                handleAccountFilterChange={() => {}}
                handleCustomerFilterChange={handleCustomerFilterChange}
              />
              <div className='row'>
                <div className='col-12 col-xl-6'>
                  {contractStatusElements.length === 1 && (
                    <div className='py-3'>
                      <span className='pr-2' style={{ color: '#216EF2' }}>
                        Contract status:
                      </span>
                      {contractStatusElements[0]}
                    </div>
                  )}
                  {contractStatusElements.length > 1 && (
                    <div className='py-3'>
                      <FormElement.RadioRow
                        id='page-new-landlord'
                        className={'pr-4'}
                        label={'Contract status'}
                        name='newlandlord'
                        elements={contractStatusElements}
                        value={contractStatus}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleContractStatusChange(e.target.value as Status);
                        }}
                        required={true}
                        style={{ color: '#216EF2' }}
                      />
                    </div>
                  )}
                </div>
                <div className='ml-auto'>
                  {showWarningMessage && (
                    <>
                      {isContractDueToExpire && (
                        <NotificationBoxV2
                          type='C1'
                          showButton={true}
                          boxText='You have contracts that are due to expire...'
                          buttonText='contact us'
                          onClick={requestAQuoteClick}
                        ></NotificationBoxV2>
                      )}
                      {isContractDeemed && (
                        <NotificationBoxV2
                          type='C2'
                          showButton={true}
                          boxText='You have contracts that are on Deemed rates...'
                          buttonText='contact us'
                          onClick={requestAQuoteClick}
                        ></NotificationBoxV2>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <GridTypeSelector initialType={state.currentType} types={gridTypes} handleTypeChange={handleTypeChange} />

      <ContactSummaryGrid
        gridData={state.gridData}
        isLoading={state.isLoading}
        hasMoreData={state.hasMoreData}
        selectedType={state.currentType}
        handleSortChange={handleSortChange}
        handlePagingChange={handlePagingChange}
        currentSortField={state.currentSortField}
        currentSortOrder={state.currentSortOrder}
      />

      <ContractSummaryModal
        show={showContractSummaryModal}
        directCustomerContactEmail='renewals@drax.com'
        tpiContactEmail='core.brokers@drax.com'
        contactNumber='01473 237870'
        onHide={handleRequestAQuoteHide}
        isDirectCustomer={isDirectCustomer && !isTpiCustomer && !isDirectSoldByBrokerCustomer}
        isDirectSoldByBrokerCustomer={isDirectSoldByBrokerCustomer && !isTpiCustomer}
        isTpiCustomer={isTpiCustomer}
      ></ContractSummaryModal>
    </>
  );
};
